// oxlint-disable-next-line typescript/triple-slash-reference -- consumers need these ambient types
/// <reference path="../declarations/vendor/http-client.d.ts" />

import {
  accept,
  base,
  createFetch,
  createStack,
  header,
  init,
  json,
  method,
  params,
} from 'http-client';
import Cookies from 'js-cookie';

type SessionErrorResponse = {
  error?: string;
  status?: number;
};

class ResponseError extends Error {
  public response: SessionErrorResponse;

  constructor(message: string, response: SessionErrorResponse) {
    super(message);
    this.response = response;
  }
}

class SessionApi {
  private BASE_URL = '/api/v1';
  private commonStack: () => ReturnType<typeof createStack>;

  constructor(cookieKey: string, railsUrl: string) {
    // N.B. this is a function because `Cookies.get` can change
    // during runtime, we don't want to use the original (or nonexistant)
    // one from JS-load time
    this.commonStack = () =>
      createStack(
        init('credentials', 'include'),
        accept('application/json'),
        header('Content-Type', 'application/json'),
        header('X-CSRF-Token', Cookies.get(cookieKey)),
        base(railsUrl),
      );

    Object.getOwnPropertyNames(Object.getPrototypeOf(this) as SessionApi)
      .filter((key) => key !== 'constructor')
      .forEach((key) => {
        const method = (this as Record<string, unknown>)[key];
        if (typeof method === 'function') {
          (this as Record<string, unknown>)[key] = method.bind(this);
        }
      });
  }

  private doWebPost(url: string, jsonParams: Record<string, unknown>): Promise<Response> {
    const stack = createStack(this.commonStack(), method('POST'), json(jsonParams));
    return createFetch(stack)(url);
  }

  private doWebDelete(url: string): Promise<Response> {
    return createFetch(this.commonStack(), method('DELETE'))(url);
  }

  private doWebApiGet(url: string, getParams?: Record<string, unknown>): Promise<Response> {
    let stack = createStack(this.commonStack());
    if (getParams) {
      stack = createStack(stack, params(getParams));
    }
    return createFetch(stack)(`${this.BASE_URL}${url}`);
  }

  private async handleResponse(res: Response) {
    const responseMessage = await res.json();
    if (res.ok) {
      return responseMessage;
    }
    const errorMessage =
      `${res.status} ${res.statusText}` +
      (responseMessage?.error ? ` ${responseMessage.error}` : '');
    throw new ResponseError(errorMessage, responseMessage);
  }

  // Either email+password, googleIDToken, or email+password+facebookIDToken is required
  //
  // If email+password+facebookIDToken is provided then web will authenticate using
  // the facebook_authenticatable warden strategy. The facebookIDToken should be the accessToken
  // provided from Facebook client-side auth
  // https://developers.facebook.com/docs/facebook-login/web
  //
  // If email+password is provided then web will authenticate using
  // the database_authenticatable warden strategy.
  //
  // If googleIDToken is provided the google_authenticatable strategy is used to authenticate.
  // The googleIDToken should be the OIDC token obtained from Google client-side auth
  // https://developers.google.com/identity/protocols/oauth2/openid-connect#obtainuserinfo
  //
  // For more details on authentication via Web check the devise configuration for the
  // Human model: https://github.com/opendoor-labs/web/blob/7fbaffaf02d03cd75a8ad57c2f1b400dacbb2782/app/models/human.rb
  public logIn(
    email?: string,
    password?: string,
    googleIDToken?: string,
    facebookIDToken?: string,
  ) {
    return this.doWebPost('/login', {
      human: { email, password },
      google_id_token: googleIDToken,
      facebook_id_token: facebookIDToken,
    }).then(this.handleResponse);
  }

  public requestHumanStatus(email: string) {
    return this.doWebApiGet(`/humans/status?email=${encodeURIComponent(email)}`).then(
      this.handleResponse,
    );
  }

  // Either email+password, googleIDToken, or email+password+facebookIDToken is required
  //
  // If email+password+facebookIDToken is provided then web will authenticate using
  // the facebook_authenticatable warden strategy. The facebookIDToken should be the accessToken
  // provided from Facebook client-side auth
  // https://developers.facebook.com/docs/facebook-login/web
  //
  // If email+password is provided then web will authenticate using
  // the database_authenticatable warden strategy.
  //
  // If googleIDToken is provided the google_authenticatable strategy is used to authenticate.
  // The googleIDToken should be the OIDC token obtained from Google client-side auth
  // https://developers.google.com/identity/protocols/oauth2/openid-connect#obtainuserinfo
  // If a user already exists with the provided email then the user will be logged in.
  //
  // For more details on authentication via Web check the devise configuration for the
  // Human model: https://github.com/opendoor-labs/web/blob/7fbaffaf02d03cd75a8ad57c2f1b400dacbb2782/app/models/human.rb
  public register(
    email?: string,
    name?: string,
    password?: string,
    phone?: string,
    googleIDToken?: string,
    facebookIDToken?: string,
    passwordlessToken?: string,
    hasGeneratedPassword?: boolean,
    redirectUrl?: string,
  ) {
    return this.doWebPost('/register', {
      email,
      full_name: name,
      primary_phone: phone,
      password,
      google_id_token: googleIDToken,
      facebook_id_token: facebookIDToken,
      passwordless_token: passwordlessToken,
      has_generated_password: hasGeneratedPassword,
      redirect_url: redirectUrl,
    }).then(this.handleResponse);
  }

  public logOut() {
    return this.doWebDelete('/logout');
  }
}

export default SessionApi;
